import { get, flatMap, map, sortBy, keyBy, filter, find, some } from 'lodash';

import Log from 'utils/Log';
import Sku from 'utils/Sku';

import {
  RECOMMENDATIONS,
  RECOMMENDATION_CONFIGS,
  COLOR_SELECTIONS,
  SIZE_SELECTIONS,
  RECOMMENDATION_TYPES,
  SIZE_CODE_MAP,
} from './config';

export {
  RECOMMENDATIONS,
  RECOMMENDATION_CONFIGS,
  COLOR_SELECTIONS,
  SIZE_SELECTIONS,
  RECOMMENDATION_TYPES,
  SIZE_CODE_MAP,
};

const Recommendation = class {
  static getPossibleRecommendations = lineItems => {
    const recommendations = flatMap(lineItems, ({ product, variant }) => {
      const recommendationList = get(RECOMMENDATIONS, [product.masterId]);

      return map(recommendationList, recommendation => ({
        config: recommendation,
        sourceProduct: product,
        sourceVariant: variant,
      }));
    });

    return sortBy(recommendations, 'config.priority');
  };

  static getRecommendations = ({ upsellMasterProducts, lineItems }) => {
    try {
      const recommendations = Recommendation.getPossibleRecommendations(lineItems);

      const sellableRecommendations = Recommendation.reconcileRecommendationsWithInventory({
        recommendations,
        upsellMasterProducts,
      });

      return sellableRecommendations;
    } catch (e) {
      Log.error(e, 'Failed to get recommendation');
      return [];
    }
  };

  /**
   * For products with different sizing schemas, we define a static map between the size codes (extracted from SKU)
   * of source and recommended products.
   * We use size codes instead of variant handles, since handles can be different for the same SKU depending on the store.
   * For example, a size 10 shoe's handle may be "10" for US and "eu-43" for EU.
   */
  static getMatchingProductSize = ({ recommendedMasterProduct, sourceProduct, sourceVariant }) => {
    // Check if mapping exists between source variant and recommended product
    const recommendedSizeCode = get(SIZE_CODE_MAP, [
      sourceProduct.type,
      recommendedMasterProduct.type,
      sourceProduct.gender,
      Sku.getSizeCodeBySku(sourceVariant.sku),
    ]);

    // If no mapping exists, return handle of source variant
    if (!recommendedSizeCode) {
      return sourceVariant.handle;
    }

    // If mapping does exist, find recommended variant corresponding to mapped size code
    const recommendedVariant = find(
      get(recommendedMasterProduct, ['products', 0, 'sizes']),
      variant =>
        Sku.getSizeCodeBySku(variant.sku, get(recommendedMasterProduct, 'type')) ===
        recommendedSizeCode
    );

    // Return handle of recommended variant
    return get(recommendedVariant, 'handle');
  };

  static reconcileRecommendationsWithInventory = ({ recommendations, upsellMasterProducts }) => {
    const masterProductMap = keyBy(upsellMasterProducts, 'id');

    const reconciledRecommendations = map(
      recommendations,
      ({ config, sourceProduct, sourceVariant }) => {
        // Find master product
        const recommendedMasterProduct = get(masterProductMap, config.masterProduct);

        // If missing, bail
        if (!recommendedMasterProduct) {
          return null;
        }

        // Determine what colors are relevant for the recommendation
        let recommendedColors = [];
        switch (config.colorSelection) {
          case COLOR_SELECTIONS.CHOOSE_COLOR:
            recommendedColors = [...recommendedMasterProduct.products];
            break;

          default:
            break;
        }

        // Determine what sizes are relevant for the recommendation
        let recommendedSizes = [];
        switch (config.sizeSelection) {
          case SIZE_SELECTIONS.MATCH_SIZE:
            recommendedSizes = [
              Recommendation.getMatchingProductSize({
                recommendedMasterProduct,
                sourceProduct,
                sourceVariant,
              }),
            ];
            break;

          case SIZE_SELECTIONS.FIRST_SIZE:
            recommendedSizes = [
              get(recommendedMasterProduct, ['products', 0, 'sizesSortOrder', 0]),
            ];
            break;

          default:
            break;
        }

        // Filter out colors that don't have available and non-backordered sizes
        recommendedColors = filter(recommendedColors, color =>
          some(recommendedSizes, size => {
            const variant = get(color, ['sizes', size]);

            if (!variant) {
              return false;
            }

            return !variant.backordered && variant.available;
          })
        );

        return {
          config,
          sourceProduct,
          sourceVariant,
          recommendedColors,
          recommendedSizes,
          recommendedMasterProduct,
        };
      }
    );

    // Remove any recommendation where no colors are available in the relevant sizes
    const sellableRecommendations = filter(
      reconciledRecommendations,
      recommendation => !!get(recommendation, ['recommendedColors', 'length'])
    );

    return sellableRecommendations;
  };
};

export default Recommendation;
